import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, HelpCircle, Settings } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import axios from 'axios';
import { SEO, FAQSchema } from '@/components/seo/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAQPage = () => {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEO translations
  const seoContent = {
    nl: {
      title: 'Veelgestelde Vragen (FAQ)',
      description: 'Vind antwoorden op veelgestelde vragen over onze webdesign diensten, prijzen, levertijden en meer.',
      noFaqs: 'Geen FAQ\'s beschikbaar.',
      manageFaqs: 'FAQ\'s beheren'
    },
    en: {
      title: 'Frequently Asked Questions (FAQ)',
      description: 'Find answers to frequently asked questions about our web design services, prices, delivery times and more.',
      noFaqs: 'No FAQs available.',
      manageFaqs: 'Manage FAQs'
    },
    fr: {
      title: 'Questions Fréquentes (FAQ)',
      description: 'Trouvez des réponses aux questions fréquentes sur nos services de conception web, prix et délais.',
      noFaqs: 'Aucune FAQ disponible.',
      manageFaqs: 'Gérer les FAQs'
    },
    de: {
      title: 'Häufig Gestellte Fragen (FAQ)',
      description: 'Finden Sie Antworten auf häufig gestellte Fragen zu unseren Webdesign-Diensten, Preisen und Lieferzeiten.',
      noFaqs: 'Keine FAQs verfügbar.',
      manageFaqs: 'FAQs verwalten'
    }
  };

  const currentSeo = seoContent[language] || seoContent.nl;

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      // Fallback to empty array if API fails
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedContent = (faq, field) => {
    // Try to get the content in the current language, fallback to Dutch
    const localizedField = `${field}_${language}`;
    return faq[localizedField] || faq[`${field}_nl`] || '';
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-white pt-32">
        <div className="max-w-3xl mx-auto px-6 flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  // Prepare FAQ data for schema
  const faqSchemaData = faqs.map(faq => ({
    question: getLocalizedContent(faq, 'question'),
    answer: getLocalizedContent(faq, 'answer')
  }));

  return (
    <main data-testid="faq-page">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords="FAQ webdesign, veelgestelde vragen website, webdesign vragen, website kosten FAQ"
      />
      {faqs.length > 0 && <FAQSchema faqs={faqSchemaData} />}
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#f8f9fc] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <HelpCircle size={16} />
              <span>{t('faqPage.badge')}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6">
              <span className="text-gradient">{t('faqPage.title')}</span>
            </h1>
            <p className="text-xl text-[#475569]">
              {t('faqPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto">
          {faqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-[#475569]">{currentSeo.noFaqs}</p>
              <Link
                to="/faq-admin"
                className="inline-flex items-center gap-2 text-[#2563EB] font-medium mt-4 hover:underline"
              >
                <Settings size={16} />
                {currentSeo.manageFaqs}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                    data-testid={`faq-button-${index}`}
                  >
                    <span className="font-semibold text-[#0f172a] pr-4">
                      {getLocalizedContent(faq, 'question')}
                    </span>
                    <ChevronDown 
                      className={`w-5 h-5 text-[#2563EB] flex-shrink-0 transition-transform ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: openIndex === index ? 'auto' : 0,
                      opacity: openIndex === index ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-[#475569] leading-relaxed">
                      {getLocalizedContent(faq, 'answer')}
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Admin link */}
          <div className="mt-8 text-center">
            <Link
              to="/faq-admin"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2563EB] transition-colors"
            >
              <Settings size={14} />
              FAQ's beheren
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#f8f9fc]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-6">
              {t('faqPage.ctaTitle')}
            </h2>
            <p className="text-lg text-[#475569] mb-8">
              {t('faqPage.ctaDescription')}
            </p>
            <Link
              to="/contact"
              className="btn-accent inline-flex items-center gap-2"
              data-testid="faq-cta"
            >
              {t('faqPage.ctaButton')}
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default FAQPage;
